// Typed wrappers for the merchant-webhook surface
// (POST/GET/disable/test/deliveries under /v1/merchants/:id/webhooks).
//
// Secret discipline (CLAUDE.md rule 2): the plaintext `secret` lives
// ONLY on the create response. The list/disable/test paths return
// `webhookItem` which has `secret_fingerprint` instead — last 8 hex
// chars of sha256(secret), enough for an operator to correlate "this
// is the one I saved" without re-exposing the key.

import { apiClient } from "./client";

export type WebhookEvent =
  | "payout.approved"
  | "payout.success"
  | "payout.failure";

export const ALL_WEBHOOK_EVENTS: WebhookEvent[] = [
  "payout.approved",
  "payout.success",
  "payout.failure",
];

export interface WebhookItem {
  id: number;
  merchant_id: number;
  url: string;
  secret_fingerprint: string;
  events: WebhookEvent[];
  description: string;
  status: "active" | "disabled";
  last_success_at: string | null;
  last_failure_at: string | null;
  last_status: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  disabled_at: string | null;
}

export interface CreateWebhookPayload {
  url: string;
  events: WebhookEvent[];
  description?: string;
}

export interface CreateWebhookResult {
  webhook: WebhookItem;
  // Hex-encoded 32-byte HMAC signing secret. Returned ONCE; the
  // RevealSecret modal copies it and drops it from memory.
  secret: string;
}

export interface WebhookDelivery {
  id: number;
  webhook_id: number;
  event_id: string;
  event_type: WebhookEvent;
  payout_id: number | null;
  attempt_count: number;
  next_attempt_at: string;
  delivered_at: string | null;
  failed_at: string | null;
  last_status: number | null;
  last_error: string;
  created_at: string;
  status: "delivered" | "failed" | "pending";
}

export async function listWebhooks(merchantId: number): Promise<WebhookItem[]> {
  const resp = await apiClient.get<{ webhooks: WebhookItem[] }>(
    `/v1/merchants/${merchantId}/webhooks`,
  );
  return resp.data.webhooks;
}

export async function createWebhook(
  merchantId: number,
  payload: CreateWebhookPayload,
): Promise<CreateWebhookResult> {
  const resp = await apiClient.post<CreateWebhookResult>(
    `/v1/merchants/${merchantId}/webhooks`,
    payload,
  );
  return resp.data;
}

export async function disableWebhook(
  merchantId: number,
  webhookId: number,
): Promise<{ ok: boolean }> {
  const resp = await apiClient.post<{ ok: boolean }>(
    `/v1/merchants/${merchantId}/webhooks/${webhookId}/disable`,
  );
  return resp.data;
}

export async function testWebhook(
  merchantId: number,
  webhookId: number,
): Promise<{ delivery_id: number; event_id: string }> {
  const resp = await apiClient.post<{ delivery_id: number; event_id: string }>(
    `/v1/merchants/${merchantId}/webhooks/${webhookId}/test`,
  );
  return resp.data;
}

export async function listDeliveries(
  merchantId: number,
  webhookId: number,
): Promise<WebhookDelivery[]> {
  const resp = await apiClient.get<{ deliveries: WebhookDelivery[] }>(
    `/v1/merchants/${merchantId}/webhooks/${webhookId}/deliveries`,
  );
  return resp.data.deliveries;
}
